import { useState, useEffect } from 'react';
import { ChevronRight, X, Plus } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { useDebounce } from '@/hooks/useDebounce';
import { searchInvoices } from '../api';
import type { InvoiceSearchResult } from '../types';

interface SensorInvoicesSectionProps {
  invoices: string[];
  onChange: (invoices: string[]) => void;
}

export function SensorInvoicesSection({
  invoices,
  onChange,
}: SensorInvoicesSectionProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [results, setResults] = useState<InvoiceSearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const debouncedSearch = useDebounce(search, 300);

  useEffect(() => {
    if (!debouncedSearch || debouncedSearch.length < 2) {
      setResults([]);
      return;
    }

    let cancelled = false;
    setIsSearching(true);
    searchInvoices(debouncedSearch)
      .then((res) => {
        if (!cancelled) {
          setResults(
            res.filter((inv) => !invoices.includes(inv.invoice_number)),
          );
        }
      })
      .catch(() => {
        if (!cancelled) setResults([]);
      })
      .finally(() => {
        if (!cancelled) setIsSearching(false);
      });

    return () => {
      cancelled = true;
    };
  }, [debouncedSearch, invoices]);

  const addInvoice = (invoiceNumber: string) => {
    if (!invoices.includes(invoiceNumber)) {
      onChange([...invoices, invoiceNumber]);
    }
    setSearch('');
    setResults([]);
  };

  const removeInvoice = (invoiceNumber: string) => {
    onChange(invoices.filter((inv) => inv !== invoiceNumber));
  };

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger className="flex items-center gap-1 text-sm font-medium text-muted-foreground hover:text-foreground w-full py-1">
        <ChevronRight
          className={`h-4 w-4 transition-transform ${open ? 'rotate-90' : ''}`}
        />
        Invoices ({invoices.length})
      </CollapsibleTrigger>
      <CollapsibleContent className="space-y-3 pt-2 pl-5">
        {invoices.length > 0 && (
          <div className="space-y-1">
            {invoices.map((inv) => (
              <div
                key={inv}
                className="flex items-center justify-between text-sm bg-muted/50 rounded px-2 py-1"
              >
                <span>{inv}</span>
                <button
                  type="button"
                  onClick={() => removeInvoice(inv)}
                  className="text-muted-foreground hover:text-destructive"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            ))}
          </div>
        )}
        {invoices.length === 0 && (
          <p className="text-sm text-muted-foreground">No Invoices</p>
        )}
        <div>
          <Label>Search invoices to add</Label>
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by invoice number..."
          />
        </div>
        {isSearching && (
          <p className="text-xs text-muted-foreground">Searching...</p>
        )}
        {results.length > 0 && (
          <div className="space-y-1 max-h-32 overflow-y-auto">
            {results.map((inv) => (
              <div
                key={inv._id}
                className="flex items-center justify-between text-sm bg-muted/30 rounded px-2 py-1"
              >
                <span>
                  {inv.invoice_number}
                  {inv.project_name && ` — ${inv.project_name}`}
                  {inv.status && ` [${inv.status}]`}
                  {inv.expiration_date && ` ${inv.expiration_date}`}
                </span>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-6 w-6 p-0"
                  onClick={() => addInvoice(inv.invoice_number)}
                >
                  <Plus className="h-3 w-3" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </CollapsibleContent>
    </Collapsible>
  );
}
